import { useState, useEffect, useCallback } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface UseThemeReturn {
  theme: Theme;
  actualTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const useTheme = (): UseThemeReturn => {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = (localStorage.getItem('zadania-app-theme') || localStorage.getItem('zadania-pro-theme')) as Theme;
      return savedTheme || 'system';
    }
    return 'system';
  });

  const getActualTheme = useCallback((): 'light' | 'dark' => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  }, [theme]);

  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>(() => getActualTheme());

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('zadania-app-theme', newTheme);
    localStorage.removeItem('zadania-pro-theme');
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(actualTheme === 'light' ? 'dark' : 'light');
  }, [actualTheme, setTheme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (theme === 'system') {
        setActualTheme(getActualTheme());
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme, getActualTheme]);

  useEffect(() => {
    setActualTheme(getActualTheme());
  }, [theme, getActualTheme]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.removeAttribute('data-theme');
    root.setAttribute('data-theme', actualTheme);
    document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', actualTheme === 'dark' ? '#1f2937' : '#ffffff');
    }
  }, [actualTheme]);

  return {
    theme,
    actualTheme,
    setTheme,
    toggleTheme,
  };
};

export default useTheme;
